import React from 'react';
import {
  AlertTriangle,
  Clock,
  Flame,
  CalendarCheck,
  FlaskConical,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { ROUTES } from '@/routes/paths';

interface AlertItem {
  id: string;
  type: 'overdue' | 'due_today' | 'hot_lead' | 'expiring_quote' | 'sample_feedback';
  title: string;
  subtitle: string;
  linkText: string;
  link: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  badgeColor: string;
}

interface PriorityAlertStripProps {
  overdueCount?: number;
  dueTodayCount?: number;
  hotLeadsCount?: number;
  expiringQuotesCount?: number;
  sampleFeedbackCount?: number;
  overdueDispatchesCount?: number;
}

export const PriorityAlertStrip: React.FC<PriorityAlertStripProps> = ({
  overdueCount = 0,
  dueTodayCount = 0,
  hotLeadsCount = 0,
  expiringQuotesCount = 0,
  sampleFeedbackCount = 0,
  overdueDispatchesCount = 0,
}) => {
  const alerts: AlertItem[] = [];

  if (overdueCount > 0) {
    alerts.push({
      id: 'overdue',
      type: 'overdue',
      title: `${overdueCount} Follow-ups Overdue`,
      subtitle: `${overdueCount} follow-up${overdueCount === 1 ? '' : 's'} past due and waiting on you`,
      linkText: 'Resolve Now',
      link: '#followup-center',
      icon: Clock,
      color: 'text-danger-text bg-danger/10 border-danger/20',
      badgeColor: 'bg-danger text-on-primary',
    });
  }

  if (dueTodayCount > 0) {
    alerts.push({
      id: 'due-today',
      type: 'due_today',
      title: `${dueTodayCount} Follow-ups Due Today`,
      subtitle: `${dueTodayCount} follow-up${dueTodayCount === 1 ? '' : 's'} scheduled for today`,
      linkText: 'View Schedule',
      link: '#followup-center',
      icon: CalendarCheck,
      color: 'text-primary bg-primary-container border-primary/20',
      badgeColor: 'bg-primary text-on-primary',
    });
  }

  if (hotLeadsCount > 0) {
    alerts.push({
      id: 'hot-leads',
      type: 'hot_lead',
      title: `${hotLeadsCount} New Leads Today`,
      subtitle: `${hotLeadsCount} fresh enquir${hotLeadsCount === 1 ? 'y needs' : 'ies need'} first contact`,
      linkText: 'Call Leads',
      link: ROUTES.pipeline,
      icon: Flame,
      color: 'text-warning-text bg-warning/10 border-amber-500/20',
      badgeColor: 'bg-warning text-on-primary',
    });
  }

  if (expiringQuotesCount > 0) {
    alerts.push({
      id: 'expiring-quotes',
      type: 'expiring_quote',
      title: `${expiringQuotesCount} Quotation${expiringQuotesCount === 1 ? '' : 's'} Ageing`,
      subtitle: `Open ${expiringQuotesCount === 1 ? 'quote has' : 'quotes have'} had no response in over a week`,
      linkText: 'Follow-up Quote',
      link: ROUTES.quotations,
      icon: AlertTriangle,
      color: 'text-warning-text bg-warning/10 border-warning/30',
      badgeColor: 'bg-warning text-on-primary',
    });
  }

  if (sampleFeedbackCount > 0) {
    alerts.push({
      id: 'sample-feedback',
      type: 'sample_feedback',
      title: `${sampleFeedbackCount} Sample${sampleFeedbackCount === 1 ? '' : 's'} Need Trial Feedback`,
      subtitle: `${sampleFeedbackCount} sample${sampleFeedbackCount === 1 ? '' : 's'} awaiting a customer verdict`,
      linkText: 'Log Feedback',
      link: ROUTES.sampling,
      icon: FlaskConical,
      color: 'text-primary bg-primary/10 border-primary/30',
      badgeColor: 'bg-primary text-on-primary',
    });
  }

  if (overdueDispatchesCount > 0) {
    alerts.push({
      id: 'overdue-dispatches',
      type: 'overdue',
      title: `${overdueDispatchesCount} Order${overdueDispatchesCount === 1 ? '' : 's'} Overdue for Dispatch`,
      subtitle: `${overdueDispatchesCount} order${overdueDispatchesCount === 1 ? ' has' : 's have'} passed dispatch date & still not shipped by company`,
      linkText: 'View Orders',
      link: ROUTES.salesOperations,
      icon: AlertTriangle,
      color: 'text-danger-text bg-danger/10 border-danger/30',
      badgeColor: 'bg-destructive text-on-primary',
    });
  }

  if (alerts.length === 0) {
    return (
      <div className="mb-5 flex items-center justify-between rounded-lg border border-success/30 bg-success/10 px-4 py-2.5 text-body-sm text-success-text shadow-xs">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-success-text" />
          <span className="font-semibold">Nothing overdue today</span>
        </div>
        <span className="text-success-text">Follow-ups and dispatch are clear</span>
      </div>
    );
  }

  const handleLinkClick = (e: React.MouseEvent, link: string) => {
    if (link.startsWith('#')) {
      e.preventDefault();
      const targetId = link.slice(1);
      const element = document.getElementById(targetId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <div className="mb-5 flex flex-col gap-2.5">
      <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {alerts.map((alert) => {
          const Icon = alert.icon;
          return (
            <div
              key={alert.id}
              className={`relative flex flex-col justify-between rounded-lg border p-3 shadow-xs transition hover:shadow-xs ${alert.color}`}
            >
              <div>
                <div className="flex items-center justify-between gap-1">
                  <div className="flex items-center gap-1.5 font-semibold text-body-sm">
                    <Icon className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{alert.title}</span>
                  </div>
                </div>
                <p className="mt-1 text-data-xs leading-tight text-on-surface-variant opacity-90 line-clamp-2">
                  {alert.subtitle}
                </p>
              </div>

              <div className="mt-2.5 pt-2 border-t border-black/5 flex items-center justify-between">
                <Link
                  to={alert.link}
                  onClick={(e) => handleLinkClick(e, alert.link)}
                  className="inline-flex items-center gap-1 text-data-xs font-semibold text-on-surface hover:underline"
                >
                  <span>{alert.linkText}</span>
                  <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
